import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Cell, makeField, NumInput, Panel, ReadValue, ReqStar, YNToggle } from "@/components/FieldInput";
import { GENDERS, MARITAL, PROVINCES } from "@/lib/schema";
import { CURRENCIES } from "@/lib/format";
import { ageFrom } from "@/lib/calc";
import { TIPS } from "@/lib/tips";
import { Tip } from "@/components/Explain";

export const AgeField = ({ id, reqId = id, person, onChange, asOf, req, ageOnly = false }) => {
  const auto = ageFrom(person.dob, asOf);
  const required = req?.all.has(reqId);
  const missing = req?.missing.has(reqId);
  return auto !== null
    ? <ReadValue id={id} label="Current age" value={auto} hint={ageOnly ? "" : "from date of birth"} required={required} missing={missing} tip={TIPS["profile-age"]} />
    : <div className={`space-y-1 ${missing ? "field-missing" : ""}`}><div className="flex items-center gap-1"><span className="field-label">Current age<ReqStar show={required} />{!ageOnly && <span className="field-hint">or enter date of birth</span>}</span><Tip text={TIPS["profile-age"]} testId={`tip-${id}`} /></div><NumInput id={id} value={person.age} onChange={onChange} /></div>;
};

const Children = ({ list, set, asOf, ageOnly }) => {
  const upd = (i, k, v) => set("profile.children", list.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  const grid = `child-grid m-row ${ageOnly ? "is-age-only" : ""}`;
  return (
    <div className="space-y-2">
      {list.length > 0 && <div className={`${grid} exp-head`}><span>Name</span>{!ageOnly && <span>Date of birth</span>}<span>Age</span><span>Dependant</span><span /></div>}
      {list.map((c, i) => (
        <div key={c.id} className={grid} data-testid={`child-row-${i}`}>
          <Cell l="Name" className="m-wide"><Input data-testid={`child-name-${i}`} value={c.name} onChange={(e) => upd(i, "name", e.target.value)} className="bg-white h-9" /></Cell>
          {!ageOnly && <Cell l="Date of birth"><Input data-testid={`child-dob-${i}`} type="date" value={c.dob} onChange={(e) => upd(i, "dob", e.target.value)} className="bg-white h-9" /></Cell>}
          <Cell l="Age">{ageFrom(c.dob, asOf) !== null ? <span className="read-value font-mono">{ageFrom(c.dob, asOf)}</span> : <NumInput id={`child-age-${i}`} value={c.age} onChange={(v) => upd(i, "age", v)} />}</Cell>
          <Cell l="Dependant"><YNToggle id={`child-dependant-${i}`} value={c.dependant} onChange={(v) => upd(i, "dependant", v)} /></Cell>
          <Cell l="" className="m-remove"><Button variant="ghost" size="icon" onClick={() => set("profile.children", list.filter((_, j) => j !== i))} data-testid={`child-remove-${i}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button></Cell>
        </div>
      ))}
      <Button variant="outline" size="sm" className="gap-1.5" data-testid="child-add-button"
        onClick={() => set("profile.children", [...list, { id: `c${Date.now()}`, name: "", dob: "", age: "", dependant: "Y" }])}><Plus className="h-3.5 w-3.5" /> Add child</Button>
    </div>
  );
};

export const ClientFamilyTab = ({ d, set, req, clientMode = false }) => {
  const f = makeField(d, set, req);
  const P = d.profile;
  return (
    <div className="tab-grid">
      <Panel title="Client" testId="panel-client">
        <div className="form-grid">
          {f("profile.name", "Full name")}
          {!clientMode && f("profile.dob", "Date of birth", "date")}
          <AgeField id="profile-age" person={P} asOf={P.meetingDate} req={req} ageOnly={clientMode} onChange={(v) => set("profile.age", v)} />
          {f("profile.gender", "Gender", "text", { options: GENDERS })}
          {f("profile.maritalStatus", "Marital status", "text", { options: MARITAL })}
          {f("profile.phone", "Phone")}
          {f("profile.email", "Email", "email")}
        </div>
      </Panel>
      <Panel title="Spouse / Partner" testId="panel-spouse">
        <div className="form-grid">
          {f("profile.spouse.name", "Full name")}
          {!clientMode && f("profile.spouse.dob", "Date of birth", "date")}
          <AgeField id="profile-spouse-age" person={P.spouse} asOf={P.meetingDate} req={req} ageOnly={clientMode} onChange={(v) => set("profile.spouse.age", v)} />
          {f("profile.spouse.gender", "Gender", "text", { options: GENDERS })}
          {f("profile.spouse.phone", "Phone")}
          {f("profile.spouse.email", "Email", "email")}
        </div>
      </Panel>
      <Panel title="Retirement horizon" testId="panel-horizon">
        <div className="form-grid">
          {f("profile.retirementAge", "Client retirement age", "number")}
          {f("profile.lifeExpectancy", "Client life expectancy", "number")}
          {f("profile.spouse.retirementAge", "Spouse retirement age", "number")}
          {f("profile.spouse.lifeExpectancy", "Spouse life expectancy", "number")}
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Client ages drive the retirement projection in Tab 3.</p>
      </Panel>
      <Panel title="Children & dependants" testId="panel-children">
        <Children list={P.children} set={set} asOf={P.meetingDate} ageOnly={clientMode} />
        <div className="form-grid mt-3">{f("profile.otherDependants", "Other dependants (e.g. parents)", "number")}</div>
      </Panel>
      <Panel title={clientMode ? "Household" : "Household & meeting"} className="col-span-full" testId="panel-household">
        <div className="form-grid form-grid-4">
          {f("profile.address", "Address")}
          {f("profile.city", "City")}
          {f("profile.province", "Province", "text", { options: PROVINCES })}
          {!clientMode && f("profile.meetingDate", "Meeting date", "date")}
          {!clientMode && f("profile.advisorName", "Advisor name")}
          {!clientMode && f("profile.currency", "Currency", "text", { options: CURRENCIES })}
          {f("profile.householdNotes", clientMode ? "Anything else about your household" : "Household notes", "textarea", { wide: true })}
          {!clientMode && f("profile.remarks", "Advisor remarks / recommendations (shown on report)", "textarea", { wide: true })}
        </div>
      </Panel>
    </div>
  );
};
